import { useState, useEffect, useCallback } from 'react';
import { GraduationCap, CaretDown, CaretRight, EyeSlash, ArrowClockwise, CircleNotch, Sparkle, PencilSimple, CheckCircle, Warning } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  question: 'Vragen',
  quote_request: 'Offerte-aanvragen',
  order: 'Orders',
  interest: 'Interesse',
  meeting_request: 'Afspraak verzoeken',
  unsubscribe: 'Afmeldingen',
  product_interest: 'Productinteresse',
  distributor_inquiry: 'Distributeur-vragen',
  other: 'Overig',
};

function CategoryRow({ row, expanded, onToggle }) {
  const editPct = row.total_sent > 0 ? Math.round((row.edited / row.total_sent) * 100) : 0;
  const label = CATEGORY_LABELS[row.category] || row.category;
  return (
    <button
      type="button"
      data-testid={`category-row-${row.category}`}
      onClick={onToggle}
      className={`w-full grid grid-cols-12 gap-3 items-center px-4 py-3 text-left border-b border-zinc-200 hover:bg-zinc-50 transition-colors ${expanded ? 'bg-indigo-50/30' : ''}`}
    >
      <div className="col-span-1">
        {expanded ? <CaretDown size={14} className="text-indigo-600" /> : <CaretRight size={14} className="text-zinc-400" />}
      </div>
      <div className="col-span-4">
        <div className="font-medium text-sm text-zinc-800">{label}</div>
        <div className="text-[10px] text-zinc-400 font-data uppercase tracking-wider">{row.category}</div>
      </div>
      <div className="col-span-2 text-center">
        <div className="font-data text-base font-bold text-zinc-800">{row.total_sent}</div>
        <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Verzonden</div>
      </div>
      <div className="col-span-2 text-center">
        <div className="font-data text-base font-bold text-emerald-700">{row.edited}</div>
        <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Bewerkt ({editPct}%)</div>
      </div>
      <div className="col-span-2 text-center">
        <div className="font-data text-base font-bold text-amber-700">{row.excluded || 0}</div>
        <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Uitgesloten</div>
      </div>
      <div className="col-span-1 text-right">
        <div className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-indigo-100 text-indigo-700 font-bold">
          {row.active_pool} actief
        </div>
      </div>
    </button>
  );
}

function ExampleCard({ ex, onExclude, onInclude }) {
  const [showOriginal, setShowOriginal] = useState(false);
  const adminBody = ((ex.draft_response || {}).body_text || '').trim();
  const aiOriginal = ((ex.original_draft || {}).body_text || '').trim();
  const hasOriginal = aiOriginal && aiOriginal !== adminBody;
  const isExcluded = !!ex.learning_excluded;

  return (
    <div
      data-testid={`example-${ex.reply_id}`}
      className={`border rounded-sm p-3 mb-2 ${isExcluded ? 'border-zinc-200 bg-zinc-50/60 opacity-70' : ex.was_edited ? 'border-emerald-200 bg-emerald-50/30' : 'border-zinc-200 bg-white'}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {ex.was_edited ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-emerald-100 text-emerald-700 font-bold">
                <PencilSimple size={9} weight="fill" /> Door admin bewerkt
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-zinc-100 text-zinc-600 font-bold">
                <Sparkle size={9} weight="fill" /> AI ongewijzigd
              </span>
            )}
            {isExcluded && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-amber-100 text-amber-800 border border-amber-200 font-bold">
                <EyeSlash size={9} weight="fill" /> Uitgesloten
              </span>
            )}
            <span className="text-[10px] text-zinc-400 font-data">
              {ex.sent_at ? new Date(ex.sent_at).toLocaleDateString('nl-NL') : '—'}
            </span>
          </div>
          <div className="text-sm font-medium text-zinc-800 truncate">{ex.subject || '—'}</div>
          <div className="text-[11px] text-zinc-500 truncate">van {ex.from_name || ex.from_email || 'onbekend'}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {hasOriginal && (
            <button
              type="button"
              onClick={() => setShowOriginal(s => !s)}
              data-testid={`toggle-original-${ex.reply_id}`}
              className="px-2 py-1 text-[11px] border border-indigo-200 text-indigo-700 rounded-sm hover:bg-indigo-50 transition-colors"
            >
              {showOriginal ? 'Verberg AI-origineel' : 'Toon AI-origineel'}
            </button>
          )}
          {isExcluded ? (
            <button
              type="button"
              onClick={() => onInclude(ex.reply_id)}
              data-testid={`include-${ex.reply_id}`}
              className="px-2 py-1 text-[11px] border border-emerald-200 text-emerald-700 rounded-sm hover:bg-emerald-50 transition-colors flex items-center gap-1"
            >
              <CheckCircle size={11} /> Heractiveer
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onExclude(ex.reply_id)}
              data-testid={`exclude-${ex.reply_id}`}
              className="px-2 py-1 text-[11px] border border-amber-200 text-amber-700 rounded-sm hover:bg-amber-50 transition-colors flex items-center gap-1"
            >
              <EyeSlash size={11} /> Sluit uit
            </button>
          )}
        </div>
      </div>

      {/* Inkomende email snippet */}
      <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-2 mb-2">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">Inkomende email</div>
        <div className="text-[11px] text-zinc-700 whitespace-pre-wrap line-clamp-4 max-h-20 overflow-y-auto">
          {(ex.body_text || '').slice(0, 500) || '—'}
        </div>
      </div>

      {/* Diff: AI origineel vs admin-versie (alleen bij toggle) */}
      {showOriginal && hasOriginal && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <div className="bg-red-50/60 border border-red-200 rounded-sm p-2">
            <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold mb-1">AI-origineel (niet goed genoeg)</div>
            <pre className="text-[11px] text-red-900 whitespace-pre-wrap font-sans leading-snug">{aiOriginal}</pre>
          </div>
          <div className="bg-emerald-50/60 border border-emerald-200 rounded-sm p-2">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Admin-versie (verzonden)</div>
            <pre className="text-[11px] text-emerald-900 whitespace-pre-wrap font-sans leading-snug">{adminBody}</pre>
          </div>
        </div>
      )}

      {/* Standaard: alleen verzonden versie */}
      {!showOriginal && (
        <div className="bg-blue-50/40 border border-blue-200 rounded-sm p-2">
          <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold mb-1">Verzonden antwoord</div>
          <pre className="text-[11px] text-zinc-800 whitespace-pre-wrap font-sans leading-snug">{adminBody || '—'}</pre>
        </div>
      )}
    </div>
  );
}

export default function ReplyLearningPage() {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [examples, setExamples] = useState({});
  const [loadingExamples, setLoadingExamples] = useState(false);
  const [error, setError] = useState('');

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API}/email/reply-learning/overview`, { credentials: 'include' });
      if (!r.ok) throw new Error('Kon overzicht niet laden');
      const data = await r.json();
      setOverview(data.categories || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const loadExamples = async (category) => {
    setLoadingExamples(true);
    try {
      const r = await fetch(`${API}/email/reply-learning/examples?category=${encodeURIComponent(category)}&limit=20`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setExamples(prev => ({ ...prev, [category]: data.examples || [] }));
      }
    } catch { /* ignore */ }
    setLoadingExamples(false);
  };

  const handleToggle = async (category) => {
    if (expanded === category) {
      setExpanded(null);
      return;
    }
    setExpanded(category);
    if (!examples[category]) {
      await loadExamples(category);
    }
  };

  const refreshCategory = async (category) => {
    await loadExamples(category);
    await fetchOverview();
  };

  const handleExclude = async (replyId, category) => {
    try {
      await fetch(`${API}/email/reply-learning/${replyId}/exclude`, { method: 'POST', credentials: 'include' });
      await refreshCategory(category);
    } catch { /* ignore */ }
  };

  const handleInclude = async (replyId, category) => {
    try {
      await fetch(`${API}/email/reply-learning/${replyId}/include`, { method: 'POST', credentials: 'include' });
      await refreshCategory(category);
    } catch { /* ignore */ }
  };

  const handleBulkExclude = async (category, scope) => {
    const labels = { unedited: 'alle ongewijzigde AI-replies', edited: 'alle door admin bewerkte replies', all: 'ALLE replies' };
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Weet je zeker dat je ${labels[scope]} in deze categorie uit de leer-pool wil halen?`)) return;
    try {
      const r = await fetch(`${API}/email/reply-learning/bulk-exclude`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, scope }),
      });
      if (r.ok) {
        const data = await r.json();
        await refreshCategory(category);
        // eslint-disable-next-line no-alert
        window.alert(`${data.modified} voorbeeld${data.modified === 1 ? '' : 'en'} uitgesloten.`);
      }
    } catch { /* ignore */ }
  };

  const handleBulkInclude = async (category) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Heractiveer alle uitgesloten voorbeelden in deze categorie?')) return;
    try {
      const r = await fetch(`${API}/email/reply-learning/bulk-include`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category }),
      });
      if (r.ok) {
        const data = await r.json();
        await refreshCategory(category);
        // eslint-disable-next-line no-alert
        window.alert(`${data.modified} voorbeeld${data.modified === 1 ? '' : 'en'} heractiveerd.`);
      }
    } catch { /* ignore */ }
  };

  return (
    <div className="animate-fade-in" data-testid="reply-learning-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight flex items-center gap-2">
            <GraduationCap size={30} weight="duotone" className="text-indigo-600" />
            Inbox AI Leer-overzicht
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Hier zie je per categorie hoeveel verzonden antwoorden in de few-shot pool zitten, hoe vaak je de AI-versie hebt bewerkt,
            en welke voorbeelden momenteel meegenomen worden in nieuwe AI-suggesties. Je kan een specifiek voorbeeld uitsluiten
            zodat het niet meer als referentie wordt gebruikt.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchOverview}
          data-testid="refresh-overview-btn"
          className="flex items-center gap-2 px-3 py-1.5 text-sm border border-zinc-200 hover:bg-zinc-50 rounded-sm"
        >
          <ArrowClockwise size={14} /> Vernieuw
        </button>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2 text-sm bg-red-50 border border-red-200 text-red-800 rounded-sm flex items-center gap-2">
          <Warning size={14} weight="fill" /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <CircleNotch size={28} className="animate-spin text-indigo-600" />
        </div>
      ) : overview.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-sm p-12 text-center text-zinc-500" data-testid="empty-overview">
          <GraduationCap size={36} className="mx-auto mb-3 text-zinc-300" weight="duotone" />
          Nog geen verzonden antwoorden om uit te leren. Zodra je AI-concepten goedkeurt en verzendt verschijnen ze hier.
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-sm overflow-hidden">
          <div className="bg-zinc-50 border-b border-zinc-200 grid grid-cols-12 gap-3 px-4 py-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
            <div className="col-span-1"></div>
            <div className="col-span-4">Categorie</div>
            <div className="col-span-2 text-center">Verzonden</div>
            <div className="col-span-2 text-center">Bewerkt</div>
            <div className="col-span-2 text-center">Uitgesloten</div>
            <div className="col-span-1 text-right">Pool</div>
          </div>
          {overview.map(row => (
            <div key={row.category}>
              <CategoryRow row={row} expanded={expanded === row.category} onToggle={() => handleToggle(row.category)} />
              {expanded === row.category && (
                <div className="bg-indigo-50/10 border-b border-zinc-200 px-4 py-3" data-testid={`examples-${row.category}`}>
                  {/* Bulk-actie balk */}
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3 pb-3 border-b border-indigo-100">
                    <div className="text-[11px] text-zinc-500 uppercase tracking-wider font-semibold">Bulk acties voor deze categorie</div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button
                        type="button"
                        data-testid={`bulk-exclude-unedited-${row.category}`}
                        onClick={() => handleBulkExclude(row.category, 'unedited')}
                        disabled={(row.total_sent - row.edited - (row.excluded || 0)) <= 0}
                        title="Sluit alle voorbeelden uit waar de admin de AI-versie ongewijzigd heeft goedgekeurd"
                        className="px-2.5 py-1 text-[11px] border border-amber-200 text-amber-700 rounded-sm hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                      >
                        <EyeSlash size={11} /> Sluit alle ongewijzigde uit
                      </button>
                      <button
                        type="button"
                        data-testid={`bulk-exclude-all-${row.category}`}
                        onClick={() => handleBulkExclude(row.category, 'all')}
                        disabled={row.active_pool <= 0}
                        className="px-2.5 py-1 text-[11px] border border-red-200 text-red-700 rounded-sm hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                      >
                        <EyeSlash size={11} /> Sluit alles uit
                      </button>
                      <button
                        type="button"
                        data-testid={`bulk-include-${row.category}`}
                        onClick={() => handleBulkInclude(row.category)}
                        disabled={(row.excluded || 0) <= 0}
                        className="px-2.5 py-1 text-[11px] border border-emerald-200 text-emerald-700 rounded-sm hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                      >
                        <CheckCircle size={11} /> Heractiveer alles
                      </button>
                    </div>
                  </div>
                  {loadingExamples && !examples[row.category] ? (
                    <div className="flex items-center justify-center py-6 text-sm text-zinc-500">
                      <CircleNotch size={16} className="animate-spin mr-2" /> Voorbeelden laden...
                    </div>
                  ) : (examples[row.category] || []).length === 0 ? (
                    <div className="text-center py-4 text-sm text-zinc-400">Geen voorbeelden voor deze categorie.</div>
                  ) : (
                    <>
                      <div className="text-xs text-zinc-500 mb-3">
                        {(examples[row.category] || []).filter(e => e.was_edited && !e.learning_excluded).length} bewerkt + {(examples[row.category] || []).filter(e => !e.was_edited && !e.learning_excluded).length} ongewijzigd · sortering: bewerkt eerst, dan datum
                      </div>
                      {examples[row.category].map(ex => (
                        <ExampleCard
                          key={ex.reply_id}
                          ex={ex}
                          onExclude={(id) => handleExclude(id, row.category)}
                          onInclude={(id) => handleInclude(id, row.category)}
                        />
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 p-3 bg-indigo-50 border border-indigo-200 text-sm text-indigo-800 flex items-center gap-2 rounded-sm">
        <Sparkle size={16} weight="fill" className="shrink-0 text-indigo-600" />
        <span>
          Per inkomende email worden de <strong>4 meest recente</strong> voorbeelden uit de pool gebruikt — bewerkte versies eerst.
          Hoe vaker je de AI corrigeert, hoe beter de stijl-match wordt voor jouw bedrijf.
        </span>
      </div>
    </div>
  );
}
